import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../hooks/useAuth';
import { INPUT_CLASS } from '../components/admin/listing/formFields';

const MIN_PASSWORD = 6;

/** /reset?oobCode=…&lang=… — choose a new password from the "Forgot password?" email (plan 16). */
export default function ResetPasswordPage() {
  const { t, i18n } = useTranslation();
  usePageTitle(t('auth.reset.title'), { noindex: true });
  const [searchParams] = useSearchParams();
  const code = searchParams.get('oobCode') ?? '';
  const { checkResetCode, finishReset } = useAuth();

  const [email, setEmail] = useState<string | null>(null);
  const [state, setState] = useState<'checking' | 'ready' | 'badLink' | 'saving' | 'done'>('checking');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  // The email says which language it was asked in.
  useEffect(() => {
    const lang = searchParams.get('lang');
    if ((lang === 'he' || lang === 'en') && i18n.language !== lang) i18n.changeLanguage(lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!code) {
      setState('badLink');
      return;
    }
    checkResetCode(code)
      .then((e) => {
        setEmail(e);
        setState('ready');
      })
      .catch(() => setState('badLink'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD) return setError(t('join.error.shortPassword', { min: MIN_PASSWORD }));
    if (password !== confirm) return setError(t('join.error.mismatch'));
    setState('saving');
    try {
      await finishReset(code, password);
      setState('done');
    } catch (err) {
      const errCode = (err as { code?: string } | undefined)?.code ?? '';
      if (errCode === 'auth/weak-password') {
        setError(t('admin.signupWeakPassword'));
        setState('ready');
      } else {
        setState('badLink');
      }
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-10">
      <div className="bg-paper rounded-[16px] border border-ink/10 p-6 space-y-4">
        <h1 className="text-xl font-semibold text-ink text-center">{t('auth.reset.title')}</h1>

        {state === 'checking' && <p className="text-sm text-ash text-center">{t('common.loading')}</p>}

        {state === 'badLink' && (
          <div className="space-y-3 text-center">
            <p className="text-sm text-ink">{t('auth.reset.badLink')}</p>
            <Link to="/login" className="text-sm text-ink underline">
              {t('auth.reset.toLogin')}
            </Link>
          </div>
        )}

        {(state === 'ready' || state === 'saving') && (
          <form onSubmit={save} className="space-y-3">
            {email && (
              <p className="text-sm text-ash text-center">
                {t('auth.reset.forEmail')} <bdi dir="ltr">{email}</bdi>
              </p>
            )}
            <input type="email" value={email ?? ''} autoComplete="username" readOnly hidden />
            <input
              type="password"
              autoComplete="new-password"
              aria-label={t('auth.reset.newPassword')}
              placeholder={t('auth.reset.newPassword')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={INPUT_CLASS}
              dir="ltr"
            />
            <input
              type="password"
              autoComplete="new-password"
              aria-label={t('join.confirmPassword')}
              placeholder={t('join.confirmPassword')}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={INPUT_CLASS}
              dir="ltr"
            />
            {error && (
              <p role="alert" className="text-sm text-danger-600">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={state === 'saving'}
              className="w-full min-h-[48px] rounded-[10px] bg-ink text-paper text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {state === 'saving' ? t('common.loading') : t('auth.reset.save')}
            </button>
          </form>
        )}

        {state === 'done' && (
          <div role="status" className="space-y-3 text-center">
            <p className="text-sm text-ink">{t('auth.reset.done')}</p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-[10px] bg-ink text-paper text-sm font-medium"
            >
              {t('auth.reset.toLogin')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
